/**
 * Channel Posts API client — B-2026-0074
 *
 * Typed wrappers for:
 *   GET    /channels/{slug}/posts
 *   POST   /channels/{slug}/posts
 *   PATCH  /channels/{slug}/posts/{post_id}
 *   DELETE /channels/{slug}/posts/{post_id}
 *   GET    /users/me/channel-feed
 *
 * All shapes mirror backend/app/schemas/channel_posts.py exactly.
 */

import { apiClient } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PostType = 'announcement' | 'update' | 'milestone';

export interface ChannelPostResponse {
  id: string;
  channel_id: string;
  author_id: string | null;
  author_display_name: string | null;
  author_role: string | null;
  body: string;
  post_type: PostType;
  pinned: boolean;
  attachment_url: string | null;
  created_at: string;
  edited_at: string | null;
  deleted: boolean;
}

/** Feed item: ChannelPostResponse + channel context fields. */
export interface ChannelFeedPost extends ChannelPostResponse {
  channel_slug: string;
  channel_name: string;
}

export interface ChannelPostCreate {
  body: string;
  post_type?: PostType;
  pinned?: boolean;
}

export interface ChannelPostUpdate {
  body?: string;
  pinned?: boolean;
}

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * List posts for a channel — pinned first, then newest-first.
 * Soft-deleted posts are included as placeholder items (deleted=true, body="").
 *
 * GET /channels/{slug}/posts
 */
export async function listChannelPosts(
  slug: string,
  options?: { limit?: number; before_id?: string },
): Promise<ChannelPostResponse[]> {
  const { data } = await apiClient.get<ChannelPostResponse[]>(
    `/channels/${slug}/posts`,
    { params: options },
  );
  return data;
}

/**
 * Create a new channel post. Owner only.
 *
 * POST /channels/{slug}/posts
 */
export async function createChannelPost(
  slug: string,
  payload: ChannelPostCreate,
): Promise<ChannelPostResponse> {
  const { data } = await apiClient.post<ChannelPostResponse>(
    `/channels/${slug}/posts`,
    payload,
  );
  return data;
}

/**
 * Edit the body and/or pin state of a channel post. Owner only.
 *
 * PATCH /channels/{slug}/posts/{post_id}
 */
export async function editChannelPost(
  slug: string,
  postId: string,
  payload: ChannelPostUpdate,
): Promise<ChannelPostResponse> {
  const { data } = await apiClient.patch<ChannelPostResponse>(
    `/channels/${slug}/posts/${postId}`,
    payload,
  );
  return data;
}

/**
 * Soft-delete a channel post (sets deleted_at). Owner only.
 *
 * DELETE /channels/{slug}/posts/{post_id}
 */
export async function deleteChannelPost(
  slug: string,
  postId: string,
): Promise<ChannelPostResponse> {
  const { data } = await apiClient.delete<ChannelPostResponse>(
    `/channels/${slug}/posts/${postId}`,
  );
  return data;
}

/**
 * Aggregate post feed across all channels the current user has joined.
 * Soft-deleted posts are excluded. Newest-first.
 *
 * GET /users/me/channel-feed
 */
export async function getMyChannelFeed(
  options?: { limit?: number; before_id?: string },
): Promise<ChannelFeedPost[]> {
  const { data } = await apiClient.get<ChannelFeedPost[]>(
    '/users/me/channel-feed',
    { params: options },
  );
  return data;
}
